import { SelectChangeEvent } from '@mui/material';

export const MAX_FILE_SIZE = 20 * 1024 * 1024;
export const MAX_TOTAL_FILES_SIZE = 100 * 1024 * 1024;
export const JOB_DOCUMENT_ARCHIVAL_DELAY_HOURS = 2160;

export interface TabItem {
  children?: React.ReactNode;
  value: number;
}

export interface TabHeader {
  label: string;
}

export interface NoticeType {
  type: 'notice-bk' | '341' | 'legal' | 'direct';
}

export interface DirectMailingType {
  type: 'direct_mail_personalized' | 'direct_mail_non_personalized';
}

export interface CourtStateType {
  label: string;
}

export interface CourtDistrictType {
  label: string;
}

export interface PDFFile {
  id: string;
  name: string;
  data: string;
  pageCount: number;
  documentDescription: string;
  documentECFDocketNumber: string;
  baseName: string;
  baseSize: number;
  baseBuffer: string;
  isOCRValid?: boolean;
  type?: string;
  paperColor?: string;
}

export interface CaseParty {
  id: string;
  name: string;
  address: string[];
  isFirstClass: boolean;
  isCertified: boolean;
  isOvernight: boolean;
  isCmEcf: boolean;
  partyType?: string;
  newJerseyRelationshipOfParty?: string;
  isExcluded?: boolean;
  source?: string;
  email?: string | null;
  /** Pre-resolved address country from the classification pipeline. */
  addressCountry?: string;
  /** Whether Bedrock confirmed this is a real postal address (user-supplied only). */
  isValidAddress?: boolean;
}

export interface CreditorsStateType {
  name: string;
  id: string;
  address: string[];
  isFirstClass: boolean;
  isCertified: boolean;
  isOvernight: boolean;
  isCmEcf: boolean;
  newJerseyRelationshipOfParty?: string;
  isExcluded?: boolean;
  descriptor: string;
  source?: string;
  isDuplicate?: boolean;
  isPreferred?: boolean;
  isUndeliverable?: boolean;
  /** Pre-resolved address country from the classification pipeline. */
  addressCountry?: string;
  /** Whether Bedrock confirmed this is a real postal address (user-supplied only). */
  isValidAddress?: boolean;
}

export interface BankruptcyClerk {
  name: string;
  address: string[];
  isFirstClass: boolean;
  isCertified: boolean;
  isOvernight: boolean;
  openHours?: string;
  webAddress?: string;
}

export interface Debtor {
  name: string;
  id: string;
  address: string[];
  isFirstClass: boolean;
  isCertified: boolean;
  isOvernight: boolean;
  usedNamesInLast8Years?: string;
  ssnOrItin: string;
  ein: string;
  bankruptcyClert: BankruptcyClerk;
}

export interface DebtorAttorney {
  name: string;
  address: string[];
}

export interface DebtorTrustee {
  name: string;
  address: string[];
  phoneNumber: string;
  email: string;
  id: string;
}

export interface DebtorBankruptcyClerk {
  fullName: string;
  addressOne: string;
  addressTwo?: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  fax: string;
  workingHours: string;
  webAddress: string;
}

export interface AttorneyObj {
  id: string;
  name: string;
  address: string[];
  isFirstClass: boolean;
  isCertified: boolean;
  isOvernight: boolean;
  email: string;
  phoneNumber: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  newJerseyRelationshipOfParty?: string;
}

export interface OrderPreferences {
  sides: string;
  format: string;
  service: string;
  inkColor: string;
  blankPage: string;
}

export interface CaseDocketEntry {
  isChecked: false;
  id: string;
  docketText: string;
  fillingDate: string;
  pageCount: string;
  inLibrary: boolean;
  docketDescription: string;
  docketReferenceNumber: string;
  docketFileLink: string;
  caseDocumentId: string;
  storedFile: {
    key: string;
    link: string;
  };
}

export interface formDataTypes {
  mailingType: string;
  isECFUnavailable: boolean;
  directMailingType: DirectMailingType | null;
  legalCaseFields: {
    plaintiff: string;
    defendant: string;
    estatePartyName: string;
  };
  courtState: string | undefined;
  courtDistrict: string;
  courtDivision: string;
  caseNumber: { textfieldValue: string, value: string };
  jointCaseNumbers: { id: string; textfieldValue: string; value: string; debtorName: string }[];
  multipleCaseNumbersToChoose: { [key: string]: string[] };
  courtJurisdiction: {
    county: string;
    state: string;
    division: string;
  };
  caseSource: {
    userSupplied: boolean;
    masterMailingMatrix: boolean;
    importCaseParties: boolean;
  };
  hasMultipleCasesToChoose: boolean;
  caseNumbers: [];
  adversaryProceeding: {
    adversaryNumber: string;
    serviceParties: string[];
    adversaryPlaintiff: string;
    adversaryDefendant: string;
    isChecked: boolean;
  };
  caseDocket: string;
  uploadedPdfs: PDFFile[];
  uploadedProofOfClaim: PDFFile[];
  uploadedBallot: PDFFile[];
  personalizedNumberOfPages: string;
  caseParties: CaseParty[];
  creditors: CreditorsStateType[];
  attorneys: AttorneyObj[];
  orderPreferences: OrderPreferences;
  caseDocketEntries: CaseDocketEntry[];
  selectedCaseDocketEntries: string[];
  caseData: any;
  returnAddresses: ReturnAddress[];
  certificatePreferences: CertificatePreferences;
  signatureBlocks: SignatureBlock[];
  caseChapter: string;
  caseConvertedChapter: string;
  caseChapterFillingDate: string;
  caseConvertedDate: string;
  bankruptcyType: string;
  proofOfClaim: string;
  subChapterV: string;
  meetingDate: string;
  meetingTime: string;
  meetingLocation: string;
  debtors: Debtor[] | [];
  debtorAttorney: AttorneyObj | null;
  debtorTrustee: DebtorTrustee | null;
  bankruptcyClerk: DebtorBankruptcyClerk;
  presumptionOfAbuse: string;
  dischargeable: string;
  creditorsProofOfClaim: string;
  governmentProofOfClaim: string;
  debtDischarge: string;
  deadlineForFillingProofOfClaim: string;
  fillingPlanSelected: string;
  hearingDate?: string;
  hearingTime?: string;
  hearingLocation?: string;
  deadlineProofOfClaimSelected: string;
  deadlineForFillingProofOfClaimSpecial: string;
  optionalDeadlineForFilingComplaint: string;
  hearingInformation?: {
    hearingDate: string;
    hearingTime: string;
    hearingLocation: string;
  };
  judgeName: string;
  newJerseyAttestation: {
    attestationType: string;
    directClientName: string;
    secondaryClientName: string;
  };
  edcaServedParty: {
    debtor: boolean;
    plaintiff: boolean;
    defendant: boolean;
    allCommitteeMembers: boolean;
    equitySecurityHolders: boolean;
    allCreditorsAndPartiesInInterestNoticeOfHearingOnly: boolean;
    onlyCreditorsThatHaveFiledClaimsNoticeOfHearingOnly: boolean;
    allCreditorsAndPartiesInInterest: boolean;
    fewerThanAllCreditors: {
      creditorsThatHaveFiledClaims: boolean;
      creditorsHoldingAllowedSecuredClaims: boolean;
      creditorsHoldingAllowedPriorityUnsecuredClaims: boolean;
      creditorsHoldingLeasesOrExecutoryContractsThatHaveBeenAssumed: boolean;
      twentyLargestCreditors: boolean;
    };
    administrativeClaimants: boolean;
    otherPartyInInterest: boolean;
    isFewerThanAllCreditorsChecked: boolean;
  };
  edcaCaseProceeding: string[];
  docketControlNumber: string;
  edcaChapter7ECFNumber: string;
  edcaChapter9ECFNumber: string;
  edcaChapter12or13ECFNumber: string;
  edcaChapter11ECFNumber: string;
  serviceCapacity: Record<string, string>;
  rule7004: Record<string, { is6A1Selected: boolean; is6A2Selected: boolean }>;
  californiaServiceDelivery: Record<
    string,
    {
      is6A1Selected: boolean;
      is6A2Selected: boolean;
      is6B1Selected: boolean;
      is6B2Selected: boolean;
      is6B3Selected: boolean;
      serviceCapacity: string;
    }
  >;
  rule5: Record<string, { is6B1Selected: boolean; is6B2Selected: boolean; is6B3Selected: boolean }>;
  userUploadedAddresses: UserUploadedAddresses[];
  userUploadedAddressFiles: number;
  includeReturnEnvelope: boolean;
  returnEnvelope: {
    name: string;
    office: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    isPostageIncluded: boolean;
  };
  hasLoadedDefaultCourt: boolean;
  isLoadingUploadedAddresses: boolean;
  includeCertificateOfService: boolean | null;
  barRuleDisclosure: string;
  clientCategory: string;
  paymentMethodId: string | undefined;
  isCaseNumberChecked: boolean;
  isBarInfoChecked: boolean;
  temporaryCasePartiesStatus: CaseParty[];
  rawCaseNumbers: string[]
}

export interface formDataErrors {
  mailingType: string;
  directMailingType: string;
  plaintiff: string;
  defendant: string;
  estatePartyName: string;
  courtState: string;
  courtDistrict: string;
  courtDivision: string;
  caseNumber: string;
  caseSource: string;
  courtJurisdiction: string;
  adversaryProceeding: {
    adversaryNumber: string;
    serviceParties: string[];
    adversaryPlaintiff: string;
    adversaryDefendant: string;
    isChecked: boolean;
  };
  caseDocket: string;
  combinedParties: string;
  uploadedPdfs: { id: string; errorMsg: string }[] | [];
  address: string;
  userUploadedAddresses: string;
  signatureBlocks: string;
  caseChapter: string;
  debtorName: string;
  caseConvertedChapter: string;
  caseChapterFillingDate: string;
  caseConvertedDate: string;
  bankruptcyType: string;
  meetingDate: string;
  meetingTime: string;
  meetingLocation: string;
  caseDocketEntries: { id: string; errorMsg: string }[] | [];
  ssnOrItin: string;
  ein: string;
  [key: string]: string | { id: string; errorMsg: string }[] | [] | {};
  bankruptcyClerk: string;
  fillingPlanSelected: string;
  hearingDate: string;
  hearingTime: string;
  hearingLocation: string;
  hearingInformation: string;
  customFileName: string;
  deadlineProofOfClaimSelected: string;
  deadlineForFillingProofOfClaimSpecial: string;
  preferences: string;
  edcaCaseProceeding: string;
  rules: string;
  edcaServedParty: string;
  serviceCapacity: string;
  attestationType: string;
  directClientName: string;
  secondaryClientName: string;
  paymentMethodId: string;
}

export const CASE_DOCKET = 'CM/ECF Case Docket';
export const USER_SUPPLIED = 'User Supplied';
export const MAX_FILES = 15;

// PDF upload enabled — all 4 integration points active (COS-25830).
export const FEATURE_PDF_UPLOAD_ENABLED = true;

export type CaseSource = typeof CASE_DOCKET | typeof USER_SUPPLIED;

interface Address {
  city: string;
  countryCode: string;
  countryName: string;
  firstFetchDate: string;
  isVisible: boolean;
  lastFetchDate: string;
  latitude: number | null;
  longitude: number | null;
  object: string;
  stateCode: string;
  stateName: string;
  streetAddress1: string;
  streetAddress2: string | null;
  zip: string;
  zip4: string | null;
}

interface PhoneNumber {
  phoneType: string;
  phoneNumber: string;
}

interface Email {
  emailId: string;
}

interface Contact {
  object: string;
  addressArray: Address[];
  phoneNumberArray: PhoneNumber[];
  emailArray: Email[]; // Assuming emails are strings
}

interface Email {
  objectId: string;
  emailId: string;
  isVisible: boolean;
}

interface PhoneNumber {
  object: string;
  phoneNumber: string;
  phoneType: string;
}

interface AttorneyContactObject {
  addressArray: Address[];
  emailArray: Email[];
  phoneNumberArray: PhoneNumber[];
}

interface PartyAttorneyAssociations {
  object: string;
  pageNumber: number;
  totalCount: number;
  totalPages: number;
  nextPageAPI: string | null;
}

export interface Party {
  name: string;
  addresses: string[];
}

export interface CreditorsContactObj {
  lbl: null;
  ord: number;
  val: string;
  childArray: [];
}

export interface Attorney {
  name: string;
  addresses: string[];
}

export interface Creditor {
  name: string;
  addresses: string[];
}

export interface Creditors {
  name: string;
  addresses: string[];
  description: string;
  isPreferred: boolean;
  isDuplicate: boolean;
  isUndeliverable: boolean;
  addressCountry?: string;
}

interface Parties {
  name: string;
  addresses: string[];
  partyType: partyType;
  email?: string | null;
  addressCountry?: string;
}

interface Attorneys {
  attorneyArray: Attorney[];
}

export interface ApiResponse {
  caseParties: Parties[];
  attorneys: Attorneys;
  creditors: Creditors[];
}

export interface ReturnAddress {
  firmName: string;
  addressOne: string;
  addressTwo: string;
  addressName: string;
  city: string;
  state: string;
  zipCode: string;
  selected: boolean;
  country: string;
  isNew?: boolean;
  id: string;
  isDefault?: boolean;
}

export interface CertificatePreferences {
  shouldAttachDocs: boolean;
  shouldAddHearingInfo: boolean;
  shouldIncludeNEF: boolean;
  shouldIncludeCustomName: boolean;
  customFileName: string;
  hearingInformation: {
    hearingDate: '';
    hearingTime: '';
    hearingLocation: '';
    responseDate: '';
  };
}

export interface SignatureBlock {
  fullName: string;
  barNumber?: string;
  firmName: string;
  clientRole: string;
  addressOne: string;
  addressTwo?: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  fax: string;
  selected: boolean;
  id: string;
  country: string;
  isDefault?: boolean;
}

export interface CourtInformationItem {
  isSelect: boolean;
  label: string;
  error?: string;
  data?: string[] | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => void;
  value: string | undefined;
  name?: string;
  disabled: boolean;
}

export interface SignatureBlocksErrors {
  fullName: string;
  barNumber: string;
  firmName: string;
  clientRole: string;
  addressOne: string;
  addressTwo?: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  country: string;
  fax: string;
}

export interface CourtLocationForm {
  state: string;
  district: string;
  division: string;
}

export interface ReturnAddressForm {
  addressName: string;
  firmName: string;
  addressOne: string;
  addressTwo: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface SignatureForm {
  fullName: string;
  barNumber?: string;
  firmName: string;
  addressOne: string;
  addressTwo: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  fax: string;
  clientRole: string;
  country: string;
  isDefault?: boolean;
}

export interface UserSignature extends SignatureForm {
  id: number;
}

export interface ReturnAddressErrors {
  firmName: string;
  addressOne: string;
  addressName: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface CourtLocationErrors {
  stateError: string;
  districtError: string;
  divisionError: string;
}

export interface BankruptcyClerkErrors {
  fullName: string;
  addressOne: string;
  addressTwo?: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  fax: string;
  workingHours: string;
  webAddress: string;
}

export interface Deadlines {
  dischargeable: string;
  creditorsProofOfClaim: string;
  governmentProofOfClaim: string;
  debtDischarge: string;
  deadlineForFillingProofOfClaim: string;
}

export interface FillingPlan {
  fillingPlanSelected: string;
  hearingInformation?: {
    hearingDate: '';
    hearingTime: '';
    hearingLocation: '';
  };
}
export interface CourtLocationErrors {
  stateError: string;
  districtError: string;
  divisionError: string;
}

export interface CurrentUser {
  accessToken: string;
  firstName: string;
  lastName: string;
  email: string;
  returnAddress: ReturnAddressForm;
  signature: SignatureForm;
  passwordUpdatedAt?: string;
  directOfficePhoneNumber?: string;
  directOfficePhoneNumberExtension?: string;
  cellPhoneNumber?: string;
  courtLocation: CourtLocationForm;
  organization: {
    type: string
  }
}

export const defaultReturnAddress: ReturnAddressForm = {
  addressName: '',
  firmName: '',
  addressOne: '',
  addressTwo: '',
  city: '',
  state: '',
  zipCode: '',
};

export const defaultSignature: SignatureForm = {
  fullName: '',
  barNumber: '',
  firmName: '',
  addressOne: '',
  addressTwo: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  email: '',
  fax: '',
  country: 'United States',
  clientRole: '',
};

export const defaultCourtLocation: CourtLocationForm = {
  state: '',
  district: '',
  division: '',
};
export interface BankruptcyClerkErrors {
  fullName: string;
  addressOne: string;
  addressTwo?: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  fax: string;
  workingHours: string;
  webAddress: string;
}

export interface Deadlines {
  dischargeable: string;
  creditorsProofOfClaim: string;
  governmentProofOfClaim: string;
  debtDischarge: string;
  deadlineForFillingProofOfClaim: string;
}

export interface FillingPlan {
  fillingPlanSelected: string;
  hearingInformation?: {
    hearingDate: '';
    hearingTime: '';
    hearingLocation: '';
  };
}

export interface JobCreationResponse {
  debtorName: string;
  trackingNumber: string;
  caseNumbers: string[];
  ipAddress: string;
}

export interface UserUploadedAddresses {
  id: string;
  name: string;
  address: string[];
  isFirstClass: boolean;
  isCertified: boolean;
  isOvernight: boolean;
  isCmEcf: boolean;
  newJerseyRelationshipOfParty?: string;
  source?: string;
  /** Pre-resolved address country from the classification pipeline. */
  addressCountry?: string;
  /** Whether Bedrock confirmed this is a real postal address. false = invalid, undefined = not yet checked. */
  isValidAddress?: boolean;
  /** User manually approved this address despite it being flagged invalid. Overrides the navigation block. Frontend-only. */
  isUserApproved?: boolean;
  /** Address has more than 5 content lines (excluding country). Must be reduced before submission. Frontend-only. */
  isTooManyLines?: boolean;
}

export interface companyInformationInterface {
  organizationType: string;
  customOrgType: string;
  companyName: string;
  countryCode: keyof typeof countryPhoneInfo;
  companyPhoneNumber: string;
  addressOne: string;
  addressTwo: string;
  city: string;
  state: string;
  zip: string;
  surveyAnswer: string;
  surveyAnswerDetail: string;
}

export interface companyInformationInterfaceErrors {
  organizationType: string;
  companyName: string;
  countryCode: string;
  companyPhoneNumber: string;
  addressOne: string;
  addressTwo: string;
  city: string;
  state: string;
  zip: string;
  surveyAnswer: string;
  bodyErrors: string;
}

export const organizationTypes = [
  'Consumer Bankruptcy',
  'Commercial Bankruptcy',
  'Chapter 13 Trusteeship',
  'Creditor Representation in Bankruptcy',
  'Probate & Estate',
  'Civil Litigation',
  'Family Law',
  'Immigration Law',
  'Trustee Company',
  'Mortgage Servicer',
  'Debt Collector',
  'Property Manager',
  'Other',
];

export const surveyOptions = [
  'Referral from colleague or peer',
  'Conference, CLE, or trade show',
  'Professional organization',
  'Online search',
  'Social media',
  'Email or mail from our team',
  'Other',
];

export const PROFESSIONAL_ORGANIZATION_OPTIONS = [
  'NACBA',
  'NACTT',
  'NABT',
  'State or Local Bar Association',
  'ABI',
  'NCBJ',
  'NAFER',
  'Other',
];

export const addressCountries = [
  'United States',
  'Canada',
  'Mexico',
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Ascension',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bonaire, Sint Eustatius, and Saba',
  'Bosnia-Herzegovina',
  'Botswana',
  'Brazil',
  'British Virgin Islands',
  'Brunei Darussalam',
  'Bulgaria',
  'Burkina Faso',
  'Burma',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Cape Verde',
  'Cayman Islands',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo, Democratic Republic of the',
  'Congo, Republic of the',
  'Costa Rica',
  "Cote d'Ivoire",
  'Croatia',
  'Cuba',
  'Curacao',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Falkland Islands',
  'Faroe Islands',
  'Fiji',
  'Finland',
  'France',
  'French Guiana',
  'French Polynesia',
  'Gabon',
  'Gambia',
  'Georgia, Republic of',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  `Korea, Democratic People's Republic of`,
  'Korea, Republic of',
  'Kosovo, Republic of',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macao',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Moldova',
  'Mongolia',
  'Montenegro',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Caledonia',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Macedonia, Republic of',
  'Norway',
  'Oman',
  'Pakistan',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Pitcairn Island',
  'Poland',
  'Portugal',
  'Qatar',
  'Reunion',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Helena',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Pierre and Miquelon',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia, Republic of',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Sint Maarten',
  'Slovak Republic',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Sudan, Republic of',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syrian Arab Republic (Syria)',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste, Democratic Republic of',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tristan da Cunha',
  'Tunisia',
  'Turkiye, Republic of',
  'Turkmenistan',
  'Turks and Caicos Islands',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Vietnam',
  'Wallis and Futuna Islands',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];

export interface DashboardFilters {
  noticeType: string;
  status: string[];
  createdAt_start: string;
  createdAt_end: string;
  searchValue: string;
  onHoldReason: string[];
  printGenStatus: string;
  service: string[];
}

export const stateMapper = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Guam: 'GU',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Northern Mariana Islands': 'MP',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Puerto Rico': 'PR',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  'Virgin Islands': 'VI',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
} as const;

export const ADDITIONAL_MML_LOGIC_COURTS = [
  'Western Michigan',
  'Eastern Michigan',
  'Northern Oklahoma',
  'Eastern Oklahoma',
  'Western Oklahoma',
  'Northern Ohio',
  'Southern Ohio',
  'Eastern Texas',
  'Western Texas',
  'Massachusetts',
];

export const RESTRICTED_DOMAINS = [
  'gmail',
  'yahoo',
  'outlook',
  'mail',
  'hotmail',
  'aol',
  'comcast',
  '13network', 
  'trustee13', 
  'earthlink', 
  'att',
  'icloud',
  'bellsouth',
  'fred',
  'juno',
  'live',
  'msn',
  'nwlink',
  'pacbell',
  'protonmail',
  'sbcglobal',
  'swbell',
  'verizon'
];

export const mapPostageType = {
  first_class: 'First Class',
  certified: 'Certified',
  mexico: 'Mexico',
  canada: 'Canada',
  international: 'International',
  first_class_overnight: 'Priority Express',
  international_overnight: 'Intl Priority Express',
};

export const mapEnvelopeType = {
  '#10': '#10',
  '6x9': '6x9',
  '9x12': '9x12',
  priority_envelope: 'Priority Envelope',
  legal_envelope: 'Legal Envelope',
  priority_box: 'Priority Box',
  express_1_day: 'Express 1 Day',
};

export const mapPriceAdjustments = {
  print_rush: 'Print Rush',
  print_color: 'Print Color',
  job_min: 'Job Min',
  edca_nj: 'EDCA/NJ',
  return_envelope: 'Return Envelope',
  '9x12_flats': '9x12 Flats',
  priority_envelope: 'Priority Envelope',
  legal_envelope: 'Legal Envelope',
  priority_box: 'Priority Box',
  electronic_rush: 'Electronic Rush',
  electronic_print_discount: 'Electronic Print Discount',
  electronic_postage_discount: 'Electronic Postage Discount',
  eletronic_rush: 'Electronic Rush',
  ballot_paper_color: 'Ballot Paper Color',
  loyalty_discount: 'Loyalty Discount',
  volume_discount_i: 'Volume Discount I',
  volume_discount_ii: 'Volume Discount II',
  volume_discount_iii: 'Volume Discount III',
  volume_discount_iv: 'Volume Discount IV',
};

export const thresholdTypeMapper = {
  total_pages_per_job: 'Total Pages (Per Job)',
  total_pages_ttm: 'Total Pages (TTM)',
  orders_per_month: 'Orders Per Month',
  consecutive_months: 'Consecutive Months',
};

export const AUTO_EXCLUDE_ADDRESSES = [
  'United States Bankruptcy Court',
  'UUnited States Bankruptcy Court',
  'USBC',
  'Clerk of the Court - USBC',
  'Pine Bluff Division',
  '4',
  '1100 Commerce Street',
  'St Paul',
  'Erin K. Lovall',
  'Michael C. Williamson',
  '200 Jefferson Avenue, Suite 413',
  'US Bankruptcy Court',
  'John Scanlan',
  '701 Broadway, Room 170',
  'Hot Springs Division',
  'Wilkesboro Division',
  'Springfield',
  'U.S. Bankruptcy Court',
  'Catherine Peek McEwen',
  'Caryl E. Delano',
  'USBC Clerks Office',
  'Winston-Salem',
  'Jeffery P. Hopkins',
  'K. Rodney May',
  '2',
  '701 Broadway Room 170',
  'United States Bankruptcy Court',
  'Boston',
  'Boise',
  'Texarkana Division',
  'Helena Division',
  'MICHAEL G. WILLIAMSON',
  'Durham',
  'USBC Clerk’s Office',
  'White Plains Division',
  'Barry S. Schermer',
  'USBC Western District of Oklahoma',
  'Charlotte Division',
  'LISA J. GAROFALO',
  'MICHAEL J. BROCK',
  'PETER M. ALDOUS',
  'ELIZABETH RUTH DEFLYER',
  '1',
  'U.S. Bankruptcy Court, Arizona',
  'Little Rock Division',
  'Boise',
  '405 E 8th Ave #2600',
  'CHRISTIAN N. GRIFFIN',
  'Poughkeepsi Division',
  'Western District of Michigan',
  'Topeka Divisional Office',
  'STEVEN T. LOIZZI JR',
  'Poughkeepsie Division',
  'Manhattan Division',
  'El Dorado Division',
  'Lexington Office',
  'U. S. Bankruptcy Court - Rutland',
  'Riverside Division',
  'Howard M. Metzenbaum U.S. Court House',
  '1001 SW 5th Ave #700',
  '271 Cadman Plaza East, Suite 1595',
  'Kansas City Divisional Office',
  'Greensboro',
  'S.J. Beaulieu, Jr.',
  'S. J. Beaulieu Jr.',
  'Sam M. Gibbons United States Courthouse',
  'Arthur F Stockton',
  'Bridgeport',
  'Hartford',
  'New Haven',
  'S. J. Beaulieu (ttr) Jr.',
  'Jonesboro Division',
  'Fort Smith Division',
  "USBC Clerk's Office",
  'Minneapolis',
  'Fayetville Division',
  'U. S. Bankruptcy Court',
  'Fergus Falls - St Paul',
  'Batesville Division',
  'Worcester',
  ' U S BANKRUPTCYCOURT',
  'Santa Ana Division',
  'Reading',
  'Fergus Falls - St Paul',
  'Duluth',
  'J Bratton Davis United States',
  'J. Bratton Davis United States',
  'Augusta Courthouse',
  'Des Moines',
  '537 Congress Street',
  'Judge KJ',
  'Roberta A. Colton',
  'Caryl E. Delano',
  'LOS ANGELES DIVISION',
  'NORTHERN DIVISION',
  'SAN FERNANDO VALLEY DIVISION',
  'BANKRUPTCY ADMINISTRATOR',
  'US BANKRUPTCY COURT ARIZONA',
  'FAYETTEVILLE DIVISION',
  'CENTRAL DIVISION',
  'DELTA DIVISION',
  '3',
  'U S BANKRUPTCY COURT',
  'MOBILE',
  '271 C CADMAN PLAZA EAST SUITE 1595',
  '290 FEDERAL PLAZA',
  'US BANKRUPTCY COURT DISTRICT OF PR',
  'MONSITA LECAROZ ARRIBAS',
  '306 Federal Building',
  '501 W TENTH STREET',
  '1100 COMMERCE STREET',
  '7',
  'WINSTON SALEM',
  'ASHEVILLE DIVISION',
  'SHELBY DIVISION',
  '111 SOUTH TENTH STREET',
  'BALTIMORE',
  '1050 SW 6TH AVE 700',
  '405 E 8TH AVE 2600',
  'PHILADELPHIA',
  '5',
  '202 HARLOW STREET',
  'WESTERN TEXAS',
  'BANKRUPTCY CLERKS OFFICE',
  'SAVANNAH COURTHOUSE',
  'BEAUMONT U S BANKRUPTCY COURT',
  'PLANO U S BANKRUPTCY COURT',
  'TYLER U S BANKRUPTCY COURT',
  'US BANKRUPTCY COURT',
  'US BANKRUPTCY COURT DISTRICT OF MT',
  'U.S. Bankruptcy Court, District of MT',
  'US BANKRUPTCY COURT DISTRICT OF MT',
  'BRUNSWICK COURTHOUSE',
  'UST',
  'U.S.T.',
  'United States Trustee',
  'U.S. Trustee',
  'U.S. Trustee',
  'US Trustee',
  'Trustee',
  'US Trustee -L7',
  'United States Trustee - TPA7',
  'DEPT OF JUSTICE',
  'U.S. Trustee (ust)',
  'United States Trustee -L7/13',
  'Office of the US Trustee',
  'Office of the U.S. Trustee',
  'Office of the U.S. Trustee',
  'United States Trustee - JAX 13/7 7',
  'Office of the U.S. Trustee/Oak',
  'United States Trustee - FTM7',
  'United States Trustee -L7',
  'U.S. TRUSTEE - LV - 7',
  'United States Trustee (SA)',
  'US Trustee’s Office',
  'U.S. Trustee’s Office',
  'Office of the United States Trustee',
  'U. S. Trustee',
  'U S Trustee',
  'U.S. TrusteeOak',
  'US Trustee 13',
  'Office of the U.S. Trustee.',
  'U.S. Trustee - Eastern District',
  'Office of the U.S. TrusteeOak',
  'Asst US Trustee Day',
  'ASST US TRUSTEE COL',
  'Patrick S Layng',
  'US Trustees Office',
  'Office of the U S Trustee',
  'US TRUSTEE UST',
  'JOHN FITZGERALD',
  'UNITED STATES TRUSTEE',
  'UNITED STATES TRUSTEE LA',
  'UNITED STATES TRUSTEE ND',
  'MONSITA LECAROZ ARRIBAS',
  'UNITED STATES TRUSTEE SV',
  'OFFICE OF US TRUSTEE',
  'OFFICE OF U S TRUSTEE',
  'UNITED STATES TRUSTEE SA12',
  '^(OFFICE OF THE US TRUSTEE)',
  'US TRUSTEE MAC',
  'UNITED STATES TRUSTEE AU12',
  'OFFICE OF THE US TRUSTEEOAK',
  'US TRUSTEE LV 7',
  'UNITED STATES TRUSTEE SA12',
  'US TRUSTEE (UST)',
  'UNITED STATES TRUSTEE AU12',
  'UNITED STATES TRUSTEE FTM713 7',
  'UNITED STATES TRUSTEE FTM713',
  'UNITED STATES TRUSTEE TPA713 7',
  'US TRUSTEE LV 11',
  'UNITED STATES TRUSTEEL713 7',
];

export const mapCaliforniaRules = {
  chapter7case: {
    name: 'Chapter 7 case (indicate below if subject to limited noticing; check all that are applicable)',
    ruleOne: {
      name: 'Rule 2002(h) Limited Noticing. Fed. R. Bankr. P. 2002(h); LBR 2002-3. (Check all that are applicable)',
      ruleOne: {
        name: 'One of the following applies: (1) This is a voluntary asset case and at least 70 days have elapsed since the order for relief; (2) This is an involuntary asset case and at least 90 days have elapsed since the order for relief; (3) This is a no asset case and at least 90 days have elapsed since the mailing of the notice of time for filing claims under Fed. R. Bankr. P. 3002(c)(5).',
      },
      ruleTwo: {
        name: 'This case is subject to an order limiting service. Fed. R.Bankr. P. Rule 2002(m). The order limiting service is docketed at ECF no  | under chapter 7',
      },
    },
  },
  chapter9case: {
    name: 'Chapter 9 case (indicate below if subject to limited noticing)',
    ruleOne: {
      name: 'This case is subject to an order limiting service. Fed. R.Bankr. P. Rule 2002(m). The order limiting service is docketed at ECF no  | under chapter 9',
    },
  },
  chapter15case: {
    name: 'Chapter 15 case',
  },
  chapter12Or13case: {
    name: 'Chapter 12 or 13 case (indicate below if subject to limited noticing; check all that are applicable)',
    ruleOne: {
      name: 'Rule 2002(h) Limited Noticing. This case is subject to limited noticing because at least 70 days have elapsed since the order for relief. Fed. R. Bankr. P. 2002(h); LBR 2002-3.7.',
    },
    ruleTwo: {
      name: 'Rule 3015(h) Limited Noticing (post-confirmation plan modification only). This case is subject to limited noticing because the debtor(s) has confirmed at least one plan and the modified plan filed herewith neither lengthens the term of, nor diminishes the dividend due general unsecured creditors, from the most recently confirmed plan. Fed. R. Bankr. P. 3015(h); LBR 3015-1(d)(3).',
    },
    ruleThree: {
      name: 'This case is subject to an order limiting service. Fed. R.Bankr. P. Rule 2002(m). The order limiting service is docketed at ECF no  | under chapter 12 or 13',
    },
  },
  chapter11case: {
    name: 'Chapter 11 (indicate below if subject to limited noticing)',
    ruleOne: {
      name: 'This case is subject to limited noticing because one or more creditors/equity holders committees have been appointed. Fed. R. Bankr. P. 2002(i); LBR 2002-4.',
    },
    ruleTwo: {
      name: 'This case is subject to an order limiting service. Fed. R.Bankr. P. Rule 2002(m). The order limiting service is docketed at ECF no  | under chapter 11',
    },
  },
  adversaryProceeding: {
    name: 'Adversary Proceeding',
  },
};

export const COS_EXPIRATION_IN_DAYS = 90;

export const stateAbbreviationToFullName = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MP: 'Northern Mariana Islands',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  VI: 'Virgin Islands',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const ROLES_FOR_SIGNATURE = [
  'Attorney at Law',
  'Lawyer',
  'Counsel',
  'Attorney for Debtor',
  'Attorney for Creditor',
  'Attorney for Plaintiff',
  'Attorney for Defendant',
  'Attorney for Respondent',
  'Attorney for Movant',
  'Panel Trustee',
  'Chapter 7 Panel Trustee',
  'Attorney for Panel Trustee',
  'SubChapter V Trustee',
  'Attorney for Chapter 13 Trustee',
  'Litigation Specialist',
  'Trial Attorney',
  'United States Trustee',
  'Assistant United States Trustee',
  'Chapter 13 Trustee',
  'Clerk of the Court',
  'Claims Agent',
  'Estate Representative',
  'Liquidation Manager',
  'Receiver',
  'Trustee',
];

export const stateZipCodes: Record<string, [number, number][]> = {
  Alabama: [[35004, 36925]],
  Alaska: [[99501, 99950]],
  Arizona: [[85001, 86556]],
  Arkansas: [[71601, 72959]],
  California: [[90001, 96162]],
  Colorado: [[80001, 81658]],
  Connecticut: [[6001, 6927]],
  Delaware: [[19701, 19980]],
  'District of Columbia': [
    [20001, 20091],
    [20201, 20599],
  ],
  Florida: [[32003, 34997]],
  Georgia: [
    [30002, 31999],
    [39813, 39897],
    [39901, 39901],
  ],
  Guam: [[96910, 96932]],
  Hawaii: [[96701, 96898]],
  Idaho: [[83201, 83877]],
  Illinois: [[60002, 62999]],
  Indiana: [[46001, 47997]],
  Iowa: [[50001, 52809]],
  Kansas: [[66002, 67954]],
  Kentucky: [[40003, 42788]],
  Louisiana: [[70001, 71497]],
  Maine: [[3901, 4992]],
  Maryland: [[20588, 21930]],
  Massachusetts: [
    [1001, 2791],
    [5501, 5501],
  ],
  Michigan: [[48001, 49971]],
  Minnesota: [[55001, 56763]],
  Mississippi: [[38601, 39776]],
  Missouri: [[63005, 65814]],
  Montana: [[59001, 59937]],
  Nebraska: [[68001, 69367]],
  Nevada: [[89001, 89883]],
  'New Hampshire': [[3031, 3897]],
  'New Jersey': [[7001, 8989]],
  'New Mexico': [[87001, 88439]],
  'New York': [
    [501, 501],
    [544, 544],
    [6390, 6390],
    [10001, 14905],
  ],
  'North Carolina': [[27006, 28909]],
  'North Dakota': [[58001, 58856]],
  'Northern Mariana Islands': [[96950, 96952]],
  Ohio: [[43001, 45999]],
  Oklahoma: [
    [73001, 73196],
    [73401, 74966],
  ],
  Oregon: [[97001, 97920]],
  Pennsylvania: [[15001, 19612]],
  'Puerto Rico': [
    [601, 606],
    [610, 614],
    [616, 617],
    [622, 624],
    [627, 627],
    [631, 631],
    [637, 638],
    [641, 641],
    [646, 647],
    [650, 650],
    [652, 653],
    [656, 656],
    [659, 660],
    [662, 662],
    [664, 664],
    [667, 667],
    [669, 670],
    [674, 674],
    [676, 678],
    [680, 683],
    [685, 685],
    [687, 688],
    [690, 690],
    [692, 694],
    [698, 698],
    [703, 705],
    [707, 707],
    [714, 721],
    [723, 723],
    [725, 742],
    [744, 745],
    [751, 751],
    [754, 754],
    [757, 757],
    [765, 767],
    [769, 769],
    [771, 773],
    [775, 775],
    [777, 778],
    [780, 780],
    [782, 786],
    [791, 792],
    [794, 795],
    [901, 902],
    [906, 929],
    [931, 931],
    [933, 937],
    [939, 940],
    [949, 963],
    [965, 966],
    [968, 971],
    [975, 979],
    [981, 988],
  ],
  'Rhode Island': [[2801, 2940]],
  'South Carolina': [[29001, 29945]],
  'South Dakota': [[57001, 57799]],
  Tennessee: [[37010, 38589]],
  Texas: [
    [73301, 73301],
    [73344, 73344],
    [73960, 73960],
    [75001, 79999],
    [88510, 88595],
  ],
  Utah: [[84001, 84791]],
  Vermont: [
    [5001, 5495],
    [5601, 5907],
  ],
  'Virgin Islands': [
    [801, 805],
    [820, 824],
    [830, 831],
    [840, 841],
    [850, 851],
  ],
  Virginia: [[20101, 24658]],
  Washington: [[98001, 99403]],
  'West Virginia': [[24701, 26886]],
  Wisconsin: [[53001, 54986]],
  Wyoming: [
    [82001, 83128],
    [83414, 83414],
  ],
};

export type partyType = 'Debtor' | 'Attorney' | 'Trustee' | 'U.S. Trustee';

export const mapPartyType = {
  Debtor: 'debtor',
  Attorney: 'attorney',
  Trustee: 'trustee',
  'U.S. Trustee': 'us_trustee',
};

export const countryPhoneInfo = {
  US: {
    code: '+1',
    mask: '(___) ___-____'
  },
  CA: {
    code: '+1',
    mask: '(___) ___-____',
  },
  MX: {
    code: '+52',
    mask: '(___) ___-____',
  },
  AC: {
    code: '+247',
    mask: '____',
  },
  AD: {
    code: '+376',
    mask: '___-___',
  },
  AE: {
    code: '+971',
    mask: '5_-___-____',
  },
  AF: {
    code: '+93',
    mask: '__-___-____',
  },
  AG: {
    code: '+1',
    mask: '(268)___-____',
  },
  AI: {
    code: '+1',
    mask: '(264)___-____',
  },
  AL: {
    code: '+355',
    mask: '(___)___-___',
  },
  AM: {
    code: '+374',
    mask: '__-___-___',
  },
  // AN: {
  //   code: '+599',
  //   mask: '___-____',
  // },
  AO: {
    code: '+244',
    mask: '(___)___-___',
  },
  // AQ: {
  //   code: '+672',
  //   mask: '1__-___',
  // },
  AR: {
    code: '+54',
    mask: '(___)___-____',
  },
  AS: {
    code: '+1',
    mask: '(684)___-____',
  },
  AT: {
    code: '+43',
    mask: '(___)___-____',
  },
  AU: {
    code: '+61',
    mask: '_-____-____',
  },
  AX: {
    code: '+358',
    mask: '18-___-____',
  },
  AW: {
    code: '+297',
    mask: '___-____',
  },
  AZ: {
    code: '+994',
    mask: '__-___-__-__',
  },
  BA: {
    code: '+387',
    mask: '__-_____',
  },
  BB: {
    code: '+1',
    mask: '(246)___-____',
  },
  BD: {
    code: '+880',
    mask: '__-___-___',
  },
  BE: {
    code: '+32',
    mask: '(___)___-___',
  },
  BF: {
    code: '+226',
    mask: '__-__-____',
  },
  BG: {
    code: '+359',
    mask: '(___)___-___',
  },
  BH: {
    code: '+973',
    mask: '____-____',
  },
  BI: {
    code: '+257',
    mask: '__-__-____',
  },
  BJ: {
    code: '+229',
    mask: '__-__-____',
  },
  BM: {
    code: '+1',
    mask: '(441)___-____',
  },
  BN: {
    code: '+673',
    mask: '___-____',
  },
  BO: {
    code: '+591',
    mask: '_-___-____',
  },
  BR: {
    code: '+55',
    mask: '(__)____-____',
  },
  BS: {
    code: '+1',
    mask: '(242)___-____',
  },
  BT: {
    code: '+975',
    mask: '17-___-___',
  },
  BW: {
    code: '+267',
    mask: '__-___-___',
  },
  BY: {
    code: '+375',
    mask: '(__)___-__-__',
  },
  BZ: {
    code: '+501',
    mask: '___-____',
  },

  CD: {
    code: '+243',
    mask: '(___)___-___',
  },
  CF: {
    code: '+236',
    mask: '__-__-____',
  },
  CG: {
    code: '+242',
    mask: '__-___-____',
  },
  CH: {
    code: '+41',
    mask: '__-___-____',
  },
  CI: {
    code: '+225',
    mask: '__-___-___',
  },
  CK: {
    code: '+682',
    mask: '__-___',
  },
  CL: {
    code: '+56',
    mask: '_-____-____',
  },
  CM: {
    code: '+237',
    mask: '____-____',
  },
  CN: {
    code: '+86',
    mask: '(___)____-____',
  },
  CO: {
    code: '+57',
    mask: '(___)___-____',
  },
  CR: {
    code: '+506',
    mask: '____-____',
  },
  CU: {
    code: '+53',
    mask: '_-___-____',
  },
  CV: {
    code: '+238',
    mask: '(___)__-__',
  },
  CW: {
    code: '+599',
    mask: '___-____',
  },
  CY: {
    code: '+357',
    mask: '__-___-___',
  },
  CZ: {
    code: '+420',
    mask: '(___)___-___',
  },
  DE: {
    code: '+49',
    mask: '(____)___-____',
  },
  DJ: {
    code: '+253',
    mask: '__-__-__-__',
  },
  DK: {
    code: '+45',
    mask: '__-__-__-__',
  },
  DM: {
    code: '+1',
    mask: '(767)___-____',
  },
  DO: {
    code: '+1',
    mask: '(809)___-____',
  },
  DZ: {
    code: '+213',
    mask: '__-___-____',
  },
  EC: {
    code: '+593',
    mask: '__-___-____',
  },
  EE: {
    code: '+372',
    mask: '____-____',
  },
  EG: {
    code: '+20',
    mask: '(___)___-____',
  },
  ER: {
    code: '+291',
    mask: '_-___-___',
  },
  ES: {
    code: '+34',
    mask: '(___)___-___',
  },
  ET: {
    code: '+251',
    mask: '__-___-____',
  },
  FI: {
    code: '+358',
    mask: '(___)___-__-__',
  },
  FJ: {
    code: '+679',
    mask: '__-_____',
  },
  FK: {
    code: '+500',
    mask: '_____',
  },
  FM: {
    code: '+691',
    mask: '___-____',
  },
  FO: {
    code: '+298',
    mask: '___-___',
  },
  FR: {
    code: '+33',
    mask: '(___)___-___',
  },
  GA: {
    code: '+241',
    mask: '_-__-__-__',
  },
  GB: {
    code: '+44',
    mask: '__-____-____',
  },
  GD: {
    code: '+1',
    mask: '(473)___-____',
  },
  GE: {
    code: '+995',
    mask: '(___)___-___',
  },
  GF: {
    code: '+594',
    mask: '_____-____',
  },
  GH: {
    code: '+233',
    mask: '(___)___-___',
  },
  GI: {
    code: '+350',
    mask: '___-_____',
  },
  GL: {
    code: '+299',
    mask: '__-__-__',
  },
  GM: {
    code: '+220',
    mask: '(___)__-__',
  },
  GN: {
    code: '+224',
    mask: '__-___-___',
  },
  GQ: {
    code: '+240',
    mask: '__-___-____',
  },
  GR: {
    code: '+30',
    mask: '(___)___-____',
  },
  GT: {
    code: '+502',
    mask: '_-___-____',
  },
  GU: {
    code: '+1',
    mask: '(671)___-____',
  },
  GW: {
    code: '+245',
    mask: '_-______',
  },
  GY: {
    code: '+592',
    mask: '___-____',
  },
  HK: {
    code: '+852',
    mask: '____-____',
  },
  HN: {
    code: '+504',
    mask: '____-____',
  },
  HR: {
    code: '+385',
    mask: '__-___-___',
  },
  HT: {
    code: '+509',
    mask: '__-__-____',
  },
  HU: {
    code: '+36',
    mask: '(___)___-___',
  },
  ID: {
    code: '+62',
    mask: '(8__)___-____',
  },
  IE: {
    code: '+353',
    mask: '(___)___-___',
  },
  IL: {
    code: '+972',
    mask: '5_-___-____',
  },
  IN: {
    code: '+91',
    mask: '(____)___-___',
  },
  IO: {
    code: '+246',
    mask: '___-____',
  },
  IQ: {
    code: '+964',
    mask: '(___)___-____',
  },
  IR: {
    code: '+98',
    mask: '(___)___-____',
  },
  IS: {
    code: '+354',
    mask: '___-____',
  },
  IT: {
    code: '+39',
    mask: '(___)____-___',
  },
  JM: {
    code: '+1',
    mask: '(876)___-____',
  },
  JO: {
    code: '+962',
    mask: '_-____-____',
  },
  JP: {
    code: '+81',
    mask: '__-____-____',
  },
  KE: {
    code: '+254',
    mask: '___-______',
  },
  KG: {
    code: '+996',
    mask: '(___)___-___',
  },
  KH: {
    code: '+855',
    mask: '__-___-___',
  },
  KI: {
    code: '+686',
    mask: '__-___',
  },
  KM: {
    code: '+269',
    mask: '__-_____',
  },
  KN: {
    code: '+1',
    mask: '(869)___-____',
  },
  KP: {
    code: '+850',
    mask: '191-___-____',
  },
  KR: {
    code: '+82',
    mask: '__-___-____',
  },
  KW: {
    code: '+965',
    mask: '____-____',
  },
  KY: {
    code: '+1',
    mask: '(345)___-____',
  },
  KZ: {
    code: '+7',
    mask: '(6__)___-__-__',
  },
  LA: {
    code: '+856',
    mask: '(20__)___-___',
  },
  LB: {
    code: '+961',
    mask: '__-___-___',
  },
  LC: {
    code: '+1',
    mask: '(758)___-____',
  },
  LI: {
    code: '+423',
    mask: '(___)___-____',
  },
  LK: {
    code: '+94',
    mask: '__-___-____',
  },
  LR: {
    code: '+231',
    mask: '__-___-___',
  },
  LS: {
    code: '+266',
    mask: '_-___-____',
  },
  LT: {
    code: '+370',
    mask: '(___)__-___',
  },
  LU: {
    code: '+352',
    mask: '(___)___-___',
  },
  LV: {
    code: '+371',
    mask: '__-___-___',
  },
  LY: {
    code: '+218',
    mask: '__-___-___',
  },
  MA: {
    code: '+212',
    mask: '__-____-___',
  },
  MC: {
    code: '+377',
    mask: '(___)___-___',
  },
  MD: {
    code: '+373',
    mask: '____-____',
  },
  ME: {
    code: '+382',
    mask: '__-___-___',
  },
  MG: {
    code: '+261',
    mask: '__-__-_____',
  },
  MH: {
    code: '+692',
    mask: '___-____',
  },
  MK: {
    code: '+389',
    mask: '__-___-___',
  },
  ML: {
    code: '+223',
    mask: '__-__-____',
  },
  MM: {
    code: '+95',
    mask: '__-___-___',
  },
  MN: {
    code: '+976',
    mask: '__-__-____',
  },
  MO: {
    code: '+853',
    mask: '____-____',
  },
  MP: {
    code: '+1',
    mask: '(670)___-____',
  },
  MQ: {
    code: '+596',
    mask: '(___)__-__-__',
  },
  MR: {
    code: '+222',
    mask: '__-__-____',
  },
  MS: {
    code: '+1',
    mask: '(664)___-____',
  },
  MT: {
    code: '+356',
    mask: '____-____',
  },
  MU: {
    code: '+230',
    mask: '___-____',
  },
  MV: {
    code: '+960',
    mask: '___-____',
  },
  MW: {
    code: '+265',
    mask: '1-___-___',
  },
  MY: {
    code: '+60',
    mask: '__-___-____',
  },
  MZ: {
    code: '+258',
    mask: '__-___-___',
  },
  NA: {
    code: '+264',
    mask: '__-___-____',
  },
  NC: {
    code: '+687',
    mask: '__-____',
  },
  NE: {
    code: '+227',
    mask: '__-__-____',
  },
  NF: {
    code: '+672',
    mask: '3__-___',
  },
  NG: {
    code: '+234',
    mask: '(___)___-____',
  },
  NI: {
    code: '+505',
    mask: '____-____',
  },
  NL: {
    code: '+31',
    mask: '__-___-____',
  },
  NO: {
    code: '+47',
    mask: '(___)__-___',
  },
  NP: {
    code: '+977',
    mask: '__-___-___',
  },
  NR: {
    code: '+674',
    mask: '___-____',
  },
  NU: {
    code: '+683',
    mask: '____',
  },
  NZ: {
    code: '+64',
    mask: '(___)___-___',
  },
  OM: {
    code: '+968',
    mask: '__-___-___',
  },
  PA: {
    code: '+507',
    mask: '___-____',
  },
  PE: {
    code: '+51',
    mask: '(___)___-___',
  },
  PF: {
    code: '+689',
    mask: '__-__-__',
  },
  PG: {
    code: '+675',
    mask: '(___)__-___',
  },
  PH: {
    code: '+63',
    mask: '(___)___-____',
  },
  PK: {
    code: '+92',
    mask: '(___)___-____',
  },
  PL: {
    code: '+48',
    mask: '(___)___-___',
  },
  PS: {
    code: '+970',
    mask: '__-___-____',
  },
  PT: {
    code: '+351',
    mask: '__-___-____',
  },
  PW: {
    code: '+680',
    mask: '___-____',
  },
  PY: {
    code: '+595',
    mask: '(___)___-___',
  },
  QA: {
    code: '+974',
    mask: '____-____',
  },
  RE: {
    code: '+262',
    mask: '_____-____',
  },
  RO: {
    code: '+40',
    mask: '__-___-____',
  },
  RS: {
    code: '+381',
    mask: '__-___-____',
  },
  RU: {
    code: '+7',
    mask: '(___)___-__-__',
  },
  RW: {
    code: '+250',
    mask: '(___)___-___',
  },
  SA: {
    code: '+966',
    mask: '5-____-____',
  },
  SB: {
    code: '+677',
    mask: '___-____',
  },
  SC: {
    code: '+248',
    mask: '_-___-___',
  },
  SD: {
    code: '+249',
    mask: '__-___-____',
  },
  SE: {
    code: '+46',
    mask: '__-___-____',
  },
  SG: {
    code: '+65',
    mask: '____-____',
  },
  SH: {
    code: '+290',
    mask: '____',
  },
  SI: {
    code: '+386',
    mask: '__-___-___',
  },
  SK: {
    code: '+421',
    mask: '(___)___-___',
  },
  SL: {
    code: '+232',
    mask: '__-______',
  },
  SM: {
    code: '+378',
    mask: '____-______',
  },
  SN: {
    code: '+221',
    mask: '__-___-____',
  },
  SO: {
    code: '+252',
    mask: '__-___-___',
  },
  SR: {
    code: '+597',
    mask: '___-____',
  },
  SS: {
    code: '+211',
    mask: '__-___-____',
  },
  ST: {
    code: '+239',
    mask: '__-_____',
  },
  SV: {
    code: '+503',
    mask: '__-__-____',
  },
  SX: {
    code: '+1',
    mask: '(721)___-____',
  },
  SY: {
    code: '+963',
    mask: '__-____-___',
  },
  SZ: {
    code: '+268',
    mask: '__-__-____',
  },
  TC: {
    code: '+1',
    mask: '(649)___-____',
  },
  TD: {
    code: '+235',
    mask: '__-__-__-__',
  },
  TG: {
    code: '+228',
    mask: '__-___-___',
  },
  TH: {
    code: '+66',
    mask: '__-___-____',
  },
  TJ: {
    code: '+992',
    mask: '__-___-____',
  },
  TK: {
    code: '+690',
    mask: '____',
  },
  TL: {
    code: '+670',
    mask: '___-____',
  },
  TM: {
    code: '+993',
    mask: '_-___-____',
  },
  TN: {
    code: '+216',
    mask: '__-___-___',
  },
  TO: {
    code: '+676',
    mask: '_____',
  },
  TR: {
    code: '+90',
    mask: '(___)___-____',
  },
  TT: {
    code: '+1',
    mask: '(868)___-____',
  },
  TV: {
    code: '+688',
    mask: '90____',
  },
  TW: {
    code: '+886',
    mask: '_-____-____',
  },
  TZ: {
    code: '+255',
    mask: '__-___-____',
  },
  UA: {
    code: '+380',
    mask: '(__)___-__-__',
  },
  UG: {
    code: '+256',
    mask: '(___)___-___',
  },
  UY: {
    code: '+598',
    mask: '_-___-__-__',
  },
  UZ: {
    code: '+998',
    mask: '__-___-____',
  },
  VA: {
    code: '+39',
    mask: '6-698-_____',
  },
  VC: {
    code: '+1',
    mask: '(784)___-____',
  },
  VE: {
    code: '+58',
    mask: '(___)___-____',
  },
  VG: {
    code: '+1',
    mask: '(284)___-____',
  },
  VI: {
    code: '+1',
    mask: '(340)___-____',
  },
  VN: {
    code: '+84',
    mask: '__-____-___',
  },
  WF: {
    code: '+681',
    mask: '__-____',
  },
  WS: {
    code: '+685',
    mask: '__-____',
  },
  YE: {
    code: '+967',
    mask: '___-___-___',
  },
  ZA: {
    code: '+27',
    mask: '__-___-____',
  },
  ZM: {
    code: '+260',
    mask: '__-___-____',
  },
  ZW: {
    code: '+263',
    mask: '_-______',
  },
};